import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { 
  FaHome, 
  FaUser, 
  FaDollarSign, 
  FaCalendarAlt, 
  FaExclamationTriangle,
  FaCheckCircle 
} from 'react-icons/fa';
import style from './RentalCard.module.css';

const RentalCard = ({ rental }) => {
  const [showModal, setShowModal] = useState(false);
  
  // Prevenir scroll del body cuando el modal está abierto
  React.useEffect(() => {
    const html = document.documentElement;
    const body = document.body;

    if (showModal) {
      html.style.overflow = 'hidden';
      html.style.height = '100%';
      body.style.overflow = 'hidden';
      body.style.height = '100%';
    } else {
      html.style.overflow = 'unset';
      html.style.height = 'auto';
      body.style.overflow = 'unset';
      body.style.height = 'auto';
    }

    return () => {
      html.style.overflow = 'unset';
      html.style.height = 'auto';
      body.style.overflow = 'unset';
      body.style.height = 'auto';
    };
  }, [showModal]);
  
  // Debug: ver qué datos está recibiendo realmente
  console.log('=== DEBUG RENTAL CARD ===');
  console.log('Datos del rental recibidos:', rental);
  console.log('Claves disponibles:', rental ? Object.keys(rental) : 'No rental data');
  
  // Validar que tenemos datos
  if (!rental || !rental.rental) {
    return (
      <div className={style.card}>
        <div className={style.noData}>
          <p>No hay datos disponibles del contrato</p>
        </div>
      </div>
    );
  }
  
  // Extraer datos de la propiedad y del contrato
  const property = rental; // Datos de la propiedad
  const contract = rental.rental; // Datos del contrato de alquiler
  
  console.log('🏠 Propiedad ID:', property.id);
  console.log('📄 Contrato ID:', contract.id);
  console.log('💰 Valor actual:', contract.currentRentValue, contract.currency);
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-AR');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'expired': return 'danger';
      case 'terminated': return 'warning';
      default: return 'info';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'active': return 'Activo';
      case 'expired': return 'Vencido';
      case 'terminated': return 'Terminado';
      case 'pending': return 'Pendiente';
      default: return status;
    }
  };

  // Calcular días hasta el vencimiento
  const daysToExpiry = () => {
    const today = new Date();
    const expiryDate = new Date(rental.rental?.contractEnd || rental.rental?.endDate);
    const diffTime = expiryDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const daysLeft = daysToExpiry();
  const isExpiringSoon = daysLeft <= 90 && daysLeft > 0;

  // Determinar el estado del contrato basado en si está cerrado
  const contractStatus = rental.cerrado?.cierre ? 'active' : 'pending';
  
  return (
    <div className={`${style.card} ${isExpiringSoon ? style.expiring : ''}`}>
      <div className={style.cardHeader}>
        <div className={style.propertyInfo}>
          <FaHome className={style.propertyIcon} />
          <div>
            <h3 className={style.propertyTitle}>
              {rental.title || `Propiedad ID: ${rental.id}`}
            </h3>
            <p className={style.propertyAddress}>
              {rental.street} {rental.number}, {rental.locality}, {rental.province}
            </p>
          </div>
        </div>
        
        <div className={`${style.status} ${style[getStatusColor(contractStatus)]}`}>
          {contractStatus === 'active' && <FaCheckCircle />}
          {contractStatus === 'expired' && <FaExclamationTriangle />}
          {getStatusText(contractStatus)}
        </div>
      </div>

      <div className={style.cardContent}>
        <div className={style.infoGrid}>
          <div className={style.infoItem}>
            <FaUser className={style.infoIcon} />
            <div>
              <span className={style.infoLabel}>Propietario</span>
              <span className={style.infoValue}>
                {rental.ownerName || 'Sin asignar'}
              </span>
            </div>
          </div>

          <div className={style.infoItem}>
            <FaDollarSign className={style.infoIcon} />
            <div>
              <span className={style.infoLabel}>Alquiler Actual</span>
              <span className={style.infoValue}>
                {formatCurrency(rental.rental?.currentRentValue || rental.rental?.rentValue || rental.price || 0)}
                <span style={{fontSize: '0.8em', color: '#6b7280'}}> {rental.rental?.currency || rental.currency}</span>
              </span>
            </div>
          </div>

          <div className={style.infoItem}>
            <FaCalendarAlt className={style.infoIcon} />
            <div>
              <span className={style.infoLabel}>Inicio Contrato</span>
              <span className={style.infoValue}>
                {rental.rental?.contractStart ? formatDate(rental.rental.contractStart) : 'No definido'}
              </span>
            </div>
          </div>

          <div className={style.infoItem}>
            <FaCalendarAlt className={style.infoIcon} />
            <div>
              <span className={style.infoLabel}>Fin Contrato</span>
              <span className={style.infoValue}>
                {rental.rental?.contractEnd ? formatDate(rental.rental.contractEnd) : 'No definido'}
              </span>
              {isExpiringSoon && (
                <span className={style.expiryWarning}>
                  ({daysLeft} días restantes)
                </span>
              )}
            </div>
          </div>

          <div className={style.infoItem}>
            <FaCalendarAlt className={style.infoIcon} />
            <div>
              <span className={style.infoLabel}>Día de Pago</span>
              <span className={style.infoValue}>
                Día {rental.rental?.paymentDueDay || 10} de cada mes
              </span>
            </div>
          </div>

          <div className={style.infoItem}>
            <FaHome className={style.infoIcon} />
            <div>
              <span className={style.infoLabel}>Tipo</span>
              <span className={style.infoValue}>
                {rental.propertyType || 'No especificado'}
              </span>
            </div>
          </div>
        </div>

        {/* Información adicional */}
        <div className={style.additionalInfo}>
          {rental.rental?.contractStart && rental.rental?.contractEnd && (
            <div className={style.contractPeriod}>
              <span>Contrato: {formatDate(rental.rental.contractStart)} - {formatDate(rental.rental.contractEnd)}</span>
            </div>
          )}
          
          {(rental.rental?.adjustmentType || rental.rental?.adjustmentFrequency) && (
            <div className={style.adjustmentInfo}>
              <span>Ajustes: {(rental.rental.adjustmentType || 'ICL').toUpperCase()} cada {rental.rental.adjustmentFrequency || 12} meses</span>
            </div>
          )}
          
          {/* Información de la propiedad */}
          <div className={style.propertyDetails}>
            <span>📍 {rental.environments} amb. | {rental.rooms} dorm. | {rental.bathrooms} baños</span>
          </div>
          
          {/* Estado del inmueble */}
          <div className={style.propertyState}>
            <span>Estado: {rental.propertyState || 'No especificado'}</span>
          </div>
        </div>
      </div>

      <div className={style.cardActions}>
        <button 
          className={style.actionButton}
          onClick={() => setShowModal(true)}
        >
          Ver Detalles
        </button>
        <Link 
          to={`/alquileres/contrato/${contract.id}/pagos`}
          className={style.actionButton}
          onClick={() => {
            console.log('🔗 Navegando a pagos con ID:', contract.id);
            console.log('🔗 Contract completo:', contract);
            console.log('🔗 Rental completo:', rental);
          }}
        >
          Gestionar Pagos
        </Link>
        <Link 
          to={`/alquileres/contrato/${contract.id}/inquilinos`}
          className={style.actionButton}
          onClick={() => console.log('🔗 Navegando a inquilinos con ID:', contract.id)}
        >
          Gestionar Inquilinos
        </Link>
      </div>

      {/* Modal de detalles */}
      {showModal && createPortal(
        <div 
          className={style.modalOverlay} 
          onClick={() => setShowModal(false)}
        >
          <div className={style.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={style.modalHeader}>
              <h2>Detalles del Contrato de Alquiler</h2>
              <button 
                className={style.closeButton}
                onClick={() => setShowModal(false)}
              >
                ×
              </button>
            </div>
            
            <div className={style.modalBody}>
              {/* Información del contrato */}
              <section className={style.section}>
                <h3>Información del Contrato</h3>
                <div className={style.detailGrid}>
                  <div className={style.detailItem}>
                    <strong>ID del Contrato:</strong> {contract.id}
                  </div>
                  <div className={style.detailItem}>
                    <strong>Estado:</strong> 
                    <span className={`${style.status} ${style[getStatusColor(contract.contractStatus)]}`}>
                      {getStatusText(contract.contractStatus)}
                    </span>
                  </div>
                  <div className={style.detailItem}>
                    <strong>Duración:</strong> {contract.contractDuration} meses
                  </div>
                  <div className={style.detailItem}>
                    <strong>Activo:</strong> {contract.isActive ? 'Sí' : 'No'}
                  </div>
                </div>
              </section>

              {/* Información financiera */}
              <section className={style.section}>
                <h3>Información Financiera</h3>
                <div className={style.detailGrid}>
                  <div className={style.detailItem}>
                    <strong>Valor inicial:</strong> 
                    {formatCurrency(contract.rentValue)} {contract.currency}
                  </div>
                  <div className={style.detailItem}>
                    <strong>Valor actual:</strong> 
                    {formatCurrency(contract.currentRentValue)} {contract.currency}
                  </div>
                  <div className={style.detailItem}>
                    <strong>Depósito:</strong> 
                    {formatCurrency(contract.depositAmount)} {contract.depositCurrency}
                  </div>
                  <div className={style.detailItem}>
                    <strong>Expensas:</strong> 
                    {contract.expensesIncluded ? 'Incluidas' : 'No incluidas'}
                    {!contract.expensesIncluded && contract.expensesAmount && 
                      ` (${formatCurrency(contract.expensesAmount)} ${contract.currency})`
                    }
                  </div>
                  <div className={style.detailItem}>
                    <strong>Día de vencimiento:</strong> {contract.paymentDueDay} de cada mes
                  </div>
                  <div className={style.detailItem}>
                    <strong>Interés por mora:</strong> {contract.lateInterestRate}% diario
                  </div>
                  <div className={style.detailItem}>
                    <strong>Período de gracia:</strong> {contract.gracePeriodDays} días
                  </div>
                </div>
              </section>

              {/* Fechas importantes */}
              <section className={style.section}>
                <h3>Fechas del Contrato</h3>
                <div className={style.detailGrid}>
                  <div className={style.detailItem}>
                    <strong>Inicio:</strong> {formatDate(contract.contractStart)}
                  </div>
                  <div className={style.detailItem}>
                    <strong>Fin:</strong> {formatDate(contract.contractEnd)}
                  </div>
                  <div className={style.detailItem}>
                    <strong>Próximo ajuste:</strong> {formatDate(contract.nextAdjustmentDate)}
                  </div>
                  <div className={style.detailItem}>
                    <strong>Fecha de creación:</strong> {formatDate(contract.createdAt)}
                  </div>
                </div>
              </section>

              {/* Ajustes */}
              <section className={style.section}>
                <h3>Configuración de Ajustes</h3>
                <div className={style.detailGrid}>
                  <div className={style.detailItem}>
                    <strong>Tipo de ajuste:</strong> {contract.adjustmentType}
                  </div>
                  <div className={style.detailItem}>
                    <strong>Frecuencia:</strong> cada {contract.adjustmentFrequency} meses
                  </div>
                  {contract.adjustmentPercentage && (
                    <div className={style.detailItem}>
                      <strong>Porcentaje fijo:</strong> {contract.adjustmentPercentage}%
                    </div>
                  )}
                </div>
              </section>

              {/* Propietario */}
              <section className={style.section}>
                <h3>Información del Propietario</h3>
                <div className={style.detailGrid}>
                  <div className={style.detailItem}>
                    <strong>Nombre:</strong> {contract.ownerName || 'No especificado'}
                  </div>
                  <div className={style.detailItem}>
                    <strong>Teléfono:</strong> {contract.ownerPhone || 'No especificado'}
                  </div>
                  <div className={style.detailItem}>
                    <strong>Email:</strong> {contract.ownerEmail || 'No especificado'}
                  </div>
                  <div className={style.detailItem}>
                    <strong>Cuenta bancaria:</strong> {contract.ownerBankAccount || 'No especificado'}
                  </div>
                </div>
              </section>

              {/* Propiedad */}
              <section className={style.section}>
                <h3>Información de la Propiedad</h3>
                <div className={style.detailGrid}>
                  <div className={style.detailItem}>
                    <strong>ID de propiedad:</strong> {contract.propertyId}
                  </div>
                  <div className={style.detailItem}>
                    <strong>Tipo:</strong> {property.propertyType}
                  </div>
                  <div className={style.detailItem}>
                    <strong>Dirección:</strong> {property.street} {property.number}
                  </div>
                  <div className={style.detailItem}>
                    <strong>Localidad:</strong> {property.locality}, {property.province}
                  </div>
                  <div className={style.detailItem}>
                    <strong>Ambientes:</strong> {property.environments}
                  </div>
                  <div className={style.detailItem}>
                    <strong>Dormitorios:</strong> {property.rooms}
                  </div>
                  <div className={style.detailItem}>
                    <strong>Baños:</strong> {property.bathrooms}
                  </div>
                  <div className={style.detailItem}>
                    <strong>Estado:</strong> {property.propertyState}
                  </div>
                </div>
              </section>

              {/* Notas y cláusulas */}
              {(contract.notes || contract.specialClauses) && (
                <section className={style.section}>
                  <h3>Notas y Cláusulas Especiales</h3>
                  {contract.notes && (
                    <div className={style.detailItem}>
                      <strong>Notas:</strong>
                      <p>{contract.notes}</p>
                    </div>
                  )}
                  {contract.specialClauses && (
                    <div className={style.detailItem}>
                      <strong>Cláusulas especiales:</strong>
                      <p>{contract.specialClauses}</p>
                    </div>
                  )}
                </section>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default RentalCard;